// frontend/src/components/BaseUnmetDemandSystemSection.js

import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid'; // Import Grid for layout
import MapViewImpact from './MapViewImpact';

// NEW: Import the AI-powered insight cards
import CardAlertImpact from './CardAlertImpact';
import CardAlertUsers from './CardAlertUsers';
import CardAlertMeasures from './CardAlertMeasures';

// Import all icons that might be used for the list view
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import WaterIcon from '@mui/icons-material/Water';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import GavelIcon from '@mui/icons-material/Gavel';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlightIcon from '@mui/icons-material/Flight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import HomeIcon from '@mui/icons-material/Home';
import CategoryIcon from '@mui/icons-material/Category';
import RoadIcon from '@mui/icons-material/EditRoad';

// --- Constants & Helpers ---
const TOP_N_LIST_PER_GROUP = 5;

export const USER_INFRASTRUCTURE_TYPES =[
    'Airport', 'Supermarket', 'Hospital', 'Clinic', 'School',
    'Police station', 'Fire station', 'Courthouse', 'Train station',
    'Government office', 'Pharmacy', 'Military base', 'Warehouse',
    'City hall', 'Communication tower', 'Water plant', 'Harbour',
    'Water reservoir', 'Power substation', 'Power station', 'Residential'
];

export const genericInfrastructureCategoryConfig = {
    POWER_SUBSTATION: { label: 'Power Substation', icon: ElectricalServicesIcon },
    POWER_STATION: { label: 'Power Station', icon: ElectricalServicesIcon },
    WATER_RESERVOIR: { label: 'Water Reservoir', icon: WaterIcon },
    WATER_PLANT: { label: 'Water Plant', icon: WaterIcon },
    TRAIN_STATION: { label: 'Train Station', icon: DirectionsTransitIcon },
    HOSPITAL: { label: 'Hospital', icon: LocalHospitalIcon },
    CLINIC: { label: 'Clinic', icon: HealthAndSafetyIcon },
    SCHOOL: { label: 'School', icon: SchoolIcon },
    POLICE_STATION: { label: 'Police Station', icon: LocalPoliceIcon },
    COURTHOUSE: { label: 'Courthouse', icon: GavelIcon },
    PHARMACY: { label: 'Pharmacy', icon: LocalPharmacyIcon },
    MILITARY_BASE: { label: 'Military Base', icon: MilitaryTechIcon },
    GOVERNMENT_OFFICE: { label: 'Government Office', icon: AccountBalanceIcon },
    CITY_HALL: { label: 'City Hall', icon: AccountBalanceIcon },
    FIRE_STATION: { label: 'Fire Station', icon: FireTruckIcon },
    HARBOUR: { label: 'Harbour', icon: DirectionsBoatIcon },
    AIRPORT: { label: 'Airport', icon: FlightIcon },
    SUPERMARKET: { label: 'Supermarket', icon: StorefrontIcon },
    COMMUNICATION_TOWER: { label: 'Communication Tower', icon: BusinessIcon },
    WAREHOUSE: { label: 'Warehouse', icon: BusinessIcon },
    RESIDENTIAL: { label: 'Residential Area', icon: RoadIcon },
    DEFAULT: { label: 'Other Infrastructure', icon: CategoryIcon },
};

export const getSeverityStyling = (severity) => {
    const s = severity?.toLowerCase();
    switch (s) {
        case 'extreme': return { chipColor: 'secondary', label: 'Extreme' };
        case 'severe':  return { chipColor: 'error', label: 'Severe' };
        case 'high':    return { chipColor: 'error', label: 'High' };
        case 'medium':  return { chipColor: 'warning', label: 'Medium' };
        case 'low':     return { chipColor: 'info', label: 'Low' };
        default:        return { chipColor: 'default', label: s ? capitalizeFirstLetter(s) : 'N/A' };
    }
};

const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
};

// --- FIX 1: Pass the whole item and check for JSON type first ---
const getInfrastructureTypeForGrouping = (item, nameMap) => {
    // 1. Prioritize the explicit type from the backend JSON
    if (item.type && item.type.toLowerCase() !== 'unknown' && item.type.trim() !== '') {
        return capitalizeFirstLetter(item.type);
    }

    // 2. Fallback to guessing by name if type is missing
    const nodeId = item.node_id;
    const nodeName = item.name || nameMap[nodeId] || '';
    if (nodeId.startsWith('RO')) return 'Residential';
    
    for (const userType of USER_INFRASTRUCTURE_TYPES) {
      if (nodeName.toLowerCase().includes(userType.toLowerCase())) return capitalizeFirstLetter(userType);
    }
    return 'Other Infrastructure';
};

const getInfraIcon = (infraTypeKey) => {
    const upperCaseKey = infraTypeKey.toUpperCase().replace(/\s/g, '_');
    return genericInfrastructureCategoryConfig[upperCaseKey]?.icon || genericInfrastructureCategoryConfig.DEFAULT.icon;
};

const processAndGroupData = (data, valueKey, nameMap) => {
    if (!data) return {};
    
    const grouped = {};
    data.forEach(item => {
        if (item.node_id && typeof item[valueKey] === 'number' && item.severity) {
            // --- FIX 2: Pass the entire item to the grouping function ---
            const infraType = getInfrastructureTypeForGrouping(item, nameMap);
            if (!grouped[infraType]) grouped[infraType] = [];
            grouped[infraType].push(item);
        }
    });

    const sortedAndSliced = {};
    for (const type in grouped) {
        sortedAndSliced[type] = grouped[type]
            .sort((a, b) => (b[valueKey] ?? -Infinity) - (a[valueKey] ?? -Infinity))
            .slice(0, TOP_N_LIST_PER_GROUP);
    }

    const orderedGroupKeys = Object.keys(sortedAndSliced).sort((a,b) => {
        const indexA = USER_INFRASTRUCTURE_TYPES.indexOf(a);
        const indexB = USER_INFRASTRUCTURE_TYPES.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
    });

    const finalGrouped = {};
    orderedGroupKeys.forEach(key => {
        if (sortedAndSliced[key] && sortedAndSliced[key].length > 0) {
            finalGrouped[key] = sortedAndSliced[key];
        }
    });
    return finalGrouped;
};

// --- Reusable List Rendering Component ---
const ImpactList = ({ title, data, nameMap, selectedFeatureId, onItemClick, valueKey, metricLabel }) => {
    const theme = useTheme();
    const hasData = Object.keys(data).length > 0;

    return (
        <>
            <Typography component="h2" variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                {title} (Top {TOP_N_LIST_PER_GROUP} per type)
            </Typography>
            {hasData ? (
                Object.entries(data).map(([infraTypeKey, topNodes]) => {
                    const InfraIconToRender = getInfraIcon(infraTypeKey);
                    return (
                        <React.Fragment key={infraTypeKey}>
                            <Typography variant="overline" sx={{ mt: 2.5, mb: 1, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: theme.palette.text.secondary }}>
                                <InfraIconToRender fontSize="small" />
                                {infraTypeKey}
                            </Typography>
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <List dense disablePadding>
                                    {topNodes.map((item, i) => {
                                        const severityStyle = getSeverityStyling(item.severity);
                                        const isSelected = selectedFeatureId === item.node_id;
                                        return (
                                            <ListItemButton selected={isSelected} onClick={() => onItemClick(item)} key={item.node_id} sx={{ borderBottom: i < topNodes.length - 1 ? `1px solid ${theme.divider}` : 'none', alignItems: 'flex-start', paddingRight: '80px', position: 'relative' }}>
                                                <ListItemText
                                                    primary={item.node_id}
                                                    secondary={
                                                        <>
                                                            {/* --- FIX 3: Smart label logic using item.name, nameMap, or item.type --- */}
                                                            <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                                                                {(item.name && item.name !== 'No Name') 
                                                                    ? item.name 
                                                                    : (nameMap[item.node_id] || item.type || 'Unknown Infrastructure')}
                                                            </Typography>
                                                            <Typography component="span" variant="caption" color="text.secondary">
                                                                {metricLabel}: {item[valueKey]?.toLocaleString() ?? 'N/A'}
                                                            </Typography>
                                                        </>
                                                    }
                                                    sx={{ mr: 2 }}
                                                />
                                                <Chip label={severityStyle.label} color={severityStyle.chipColor} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' }}/>
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Paper>
                        </React.Fragment>
                    );
                })
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No infrastructures with {metricLabel.toLowerCase()} to display.
                </Typography>
            )}
        </>
    );
};


// --- Base Component for a Single System Type ---
export const BaseUnmetDemandSystemSection = ({
    analysisData,
    nameMap,
    systemKey,
    sectionTitle,
    systemIcon: SystemIconComponent,
    systemColor,
    floodData,
    scenarioId,
    scenarioName,
    hazardLabel,
    llmInsights,
    loadingLlm,
}) => {
    const [selectedFeatureForMap, setSelectedFeatureForMap] = useState(null);
    const mapCardRef = useRef(null);

    const handleListItemClick = useCallback((itemFromList) => {
        if (itemFromList.lat != null && itemFromList.lon != null) {
            const featureForMap = {
                feature_id: itemFromList.node_id,
                name: nameMap[itemFromList.node_id] || itemFromList.node_id,
                coords:[itemFromList.lon, itemFromList.lat],
                properties: itemFromList 
            };
            setSelectedFeatureForMap(featureForMap);
            if (mapCardRef.current) {
                mapCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            console.warn("Cannot select feature on map: coordinates are missing.", itemFromList);
        }
    }, [nameMap]);

    const groupedUnmetNodes = useMemo(() => {
        const data = analysisData?.[systemKey]?.unmet_demand_nodes ||[];
        return processAndGroupData(data, 'absolute_unmet', nameMap);
    },[analysisData, systemKey, nameMap]);

    const groupedRestrictedNodes = useMemo(() => {
        const data = analysisData?.[systemKey]?.restricted_supply_nodes ||[];
        const filteredData = data.filter(item => !item.node_id.startsWith('RO'));
        return processAndGroupData(filteredData, 'absolute_restricted', nameMap);
    }, [analysisData, systemKey, nameMap]);

    // NEW: Calculate the highest risk level for real-time analysis
    const highestRiskLevel = useMemo(() => {
        if (scenarioId) return null; // Not needed for scenarios
        if (!floodData?.features?.length) return 'N/A';
        
        const riskLevels = new Set(floodData.features.map(f => f.properties.risk_level?.toLowerCase()));
        
        if (riskLevels.has('high')) return 'high';
        if (riskLevels.has('moderate')) return 'moderate';
        if (riskLevels.has('low')) return 'low';
        return 'N/A';
    },[floodData, scenarioId]);

    const getAlertMessage = () => {
        const unmetNodes = analysisData?.[systemKey]?.unmet_demand_nodes || [];
        const restrictedNodes = analysisData?.[systemKey]?.restricted_supply_nodes || [];
        
        const unmetCount = unmetNodes.length;
        const restrictedCount = restrictedNodes.length;

        // Calculate the actual total unmet demand and restricted supply
        const totalUnmet = unmetNodes.reduce((sum, item) => sum + (item.absolute_unmet || 0), 0);
        const totalRestricted = restrictedNodes.reduce((sum, item) => sum + (item.absolute_restricted || 0), 0);

        return `Analysis found an estimated total unmet demand of ${Math.round(totalUnmet).toLocaleString()} across ${unmetCount} infrastructure(s) and a total restricted supply of ${Math.round(totalRestricted).toLocaleString()} across ${restrictedCount} infrastructure(s). Click an item on the list under the map to focus on the map.`;
    };

    return (
        <Card sx={{mb: 3}}>
            <CardHeader
                title={sectionTitle}
                // NEW: Dynamic subheader for context
                subheader={
                    scenarioId 
                    ? `Scenario: ${scenarioName || '...'} | Hazard: ${hazardLabel || '...'}`
                    : `Real-time analysis for current ${highestRiskLevel} or higher risk areas`
                }
                titleTypographyProps={{ variant: 'h6' }}
                subheaderTypographyProps={{ variant: 'caption', sx: { mt: 0.5 } }}
                avatar={<SystemIconComponent style={{ color: systemColor }} />}
            />
            <CardContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {getAlertMessage()}
                </Alert>

                {/* MODIFICATION: Replaced old LLM text with a grid of dedicated insight cards */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <CardAlertImpact
                                content={llmInsights?.potential_impacts}
                                loading={loadingLlm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <CardAlertUsers
                                content={llmInsights?.affected_users}
                                loading={loadingLlm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4}>
                            <CardAlertMeasures
                                content={llmInsights?.recommended_measures}
                                loading={loadingLlm}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Card ref={mapCardRef} variant="outlined" sx={{ width: '100%', height: 640, mb: 3 }}>
                    <MapViewImpact
                        key={`${systemKey}-${scenarioId || 'realtime'}-${selectedFeatureForMap?.feature_id || 'map'}`}
                        system={systemKey}
                        selectedFeature={selectedFeatureForMap}
                        floodData={floodData}
                        scenarioId={scenarioId}
                    />
                </Card>
                
                <ImpactList
                    title="Infrastructures sorted by unmet demand"
                    data={groupedUnmetNodes}
                    nameMap={nameMap}
                    selectedFeatureId={selectedFeatureForMap?.feature_id}
                    onItemClick={handleListItemClick}
                    valueKey="absolute_unmet"
                    metricLabel="Unmet Demand"
                />
                
            </CardContent>
        </Card>
    );
};